import { useState, useEffect, useCallback, useRef } from 'react';

export const useCombinedAnalytics = (token, timeRange) => {
  const [loading, setLoading] = useState(true);
  const componentMountedRef = useRef(true);

  const [stats, setStats] = useState(null);
  const [clientStats, setClientStats] = useState({
    total: 0,
    active: 0,
    totalBytesIn: 0,
    totalBytesOut: 0
  });
  const [connections, setConnections] = useState([]);
  const [ipStats, setIpStats] = useState([]);

  const fetchOverviewStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(
        `/api/user/analytics?range=${timeRange}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [token, timeRange]);

  const fetchClientStats = useCallback(async () => {
    if (!token) return;
    
    const clientTimeRange = timeRange === '1day' ? 'past_day' : timeRange === '7days' ? 'past_week' : 'past_month';

    try {
      const [analyticsRes, ipsRes] = await Promise.all([
        fetch(`/api/v1/analytics/clients?range=${clientTimeRange}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/v1/analytics/clients/ips?range=${clientTimeRange}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (analyticsRes.ok) {
        const data = await analyticsRes.json();
        setConnections(data.connections || []);
        setClientStats({
          total: data.total || 0,
          active: data.active || 0,
          totalBytesIn: data.total_bytes_in || 0,
          totalBytesOut: data.total_bytes_out || 0
        });
      }

      if (ipsRes.ok) {
        const ipsData = await ipsRes.json();
        setIpStats(ipsData.ips || []);
      }
    } catch (err) {
      console.error('Error fetching client stats:', err);
    }
  }, [token, timeRange]);

  const loadData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchOverviewStats(), fetchClientStats()]);
    setLoading(false);
  }, [fetchOverviewStats, fetchClientStats]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    componentMountedRef.current = true;
    
    const interval = setInterval(() => {
      if (componentMountedRef.current && !document.querySelector('.fixed.inset-0.z-50, .fixed.inset-0.z-\\[100\\]')) {
        fetchOverviewStats();
        fetchClientStats();
      }
    }, 15000);

    return () => {
      componentMountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchOverviewStats, fetchClientStats]);

  return {
    loading,
    stats,
    clientStats,
    connections,
    ipStats
  };
};

export const processChartData = (connections, timeRange, clientStatsActive) => {
  if (!connections || connections.length === 0) {
    const now = new Date();
    const categories = [];
    const inData = [];
    const outData = [];
    const activeData = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setHours(date.getHours() - i);
      categories.push(date.toISOString());
      inData.push(0);
      outData.push(0);
      activeData.push(i === 0 ? clientStatsActive : 0);
    }
    
    return { categories, inData, outData, activeData };
  }

  const hourlyData = {};
  const now = new Date();
  const points = timeRange === '1day' ? 24 : timeRange === '7days' ? 7 : 30;
  
  for (let i = points - 1; i >= 0; i--) {
    const date = new Date(now);
    if (timeRange === '1day') {
      date.setHours(date.getHours() - i, 0, 0, 0);
    } else {
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
    }
    const key = date.toISOString();
    hourlyData[key] = { bytesIn: 0, bytesOut: 0, active: 0 };
  }

  connections.forEach(conn => {
    const connDate = new Date(conn.connected_at);
    let key;
    
    if (timeRange === '1day') {
      connDate.setMinutes(0, 0, 0);
      key = connDate.toISOString();
    } else {
      connDate.setHours(0, 0, 0, 0);
      key = connDate.toISOString();
    }
    
    if (hourlyData[key]) {
      hourlyData[key].bytesIn += conn.bytes_in || 0;
      hourlyData[key].bytesOut += conn.bytes_out || 0;
      if (conn.is_active) {
        hourlyData[key].active += 1;
      }
    }
  });

  const categories = Object.keys(hourlyData).sort();
  const inData = categories.map(k => Math.round((hourlyData[k].bytesIn || 0) / 1024));
  const outData = categories.map(k => Math.round((hourlyData[k].bytesOut || 0) / 1024));
  const activeData = categories.map((k, idx) => {
    if (idx === categories.length - 1) {
      return clientStatsActive;
    }
    return hourlyData[k].active;
  });

  return { categories, inData, outData, activeData };
};
